import fetch from 'isomorphic-fetch';

import getCsrfToken from '~/helpers/csrf';
import { ResponseException, captureExceptionsToSentry } from '~/helpers/exceptionHandler';
import { flashMessages, genericErrorMessage } from '~/helpers/messages';

export const toCamel = str => str.replace(/([-_])([a-z])/ig, x => `${x[1].toUpperCase()}`);
export const toSnake = str => str.replace(/([a-z])([A-Z])/g, x => `${x[0]}_${x[1].toLowerCase()}`);
export const toSnakeDash = str => str.replace(/([a-z])([A-Z])/g, x => `${x[0]}-${x[1].toLowerCase()}`);

function isObject(obj) {
  return obj === Object(obj) && !Array.isArray(obj) && typeof obj !== 'function';
}

export function convertObjectCase(obj, converter) {
  if (isObject(obj)) {
    const n = {};

    Object.keys(obj)
      .forEach(k => {
        n[converter(k)] = convertObjectCase(obj[k], converter);
      });

    return n;
  } if (Array.isArray(obj)) {
    return obj.map(i => convertObjectCase(i, converter));
  }

  return obj;
}

export function convertToCamel(obj) {
  return convertObjectCase(obj, toCamel);
}

export function convertToSnake(obj) {
  return convertObjectCase(obj, toSnake);
}


export const getDaffyFetchOptions = options => {
  const finalOptionsForFetch = {
    method: 'GET',
    ...options,
    credentials: 'same-origin',
    headers: {
      'X-Requested-With': 'XMLHttpRequest',
      'X-CSRFToken': getCsrfToken(),
      ...(options.headers || {}),
    },
  };

  if (options.requestBodySnakeCase) {
    if (finalOptionsForFetch?.body && finalOptionsForFetch.headers['Content-type'] === 'application/json') {
      finalOptionsForFetch.body = JSON.stringify(convertToSnake(finalOptionsForFetch.body));
    }
  }

  return finalOptionsForFetch;
};

export const handleDaffyFetchResponse = async (response, options) => {
  // return the response object for specific handling
  if (options?.returnResponse) {
    return response;
  }
  const createMessage = options.createMessage || flashMessages;
  if ((response.status >= 200 && response.status < 300) || !!options.enhancedErrorResponse) {
    if (Object.keys(options).includes('json') && !options.json) {
      // If options: { json: false }, just return the response as is without JSON parsing
      return response.text();
    }

    // By default, handle all responses as JSON
    let data;
    try {
      data = await response.json();
      if (options.camelCase) {
        data = {
          ...convertToCamel(data),
          // If response body contains permissions, keep them from being converted to camel
          // case to maintain backwards compatible with legacy front end permissions checks
          ...(data.permissions ? { permissions: data.permissions } : {}),
        };
      }

      if (data.result && data.result === 'csrf_failure') {
        window.location.href = data.redirect_url;
      }
    } catch (error) {
      throw response;
    }

    if (options.enhancedErrorResponse && response.status >= 300) {
      throw data;
    } else {
      return data;
    }
  }

  if (!options.silent) {
    if (response.status === 401) {
      return window.location.replace('/login');
    }
    if (response.status === 403) {
      createMessage(['Data request forbidden. You don\'t have appropriate permissions.']);
    } else {
      genericErrorMessage(createMessage);
    }

    throw new ResponseException(response, {
      method: options.method || 'GET',
      status: response.status,
      url: options.url,
      originatedUrl: options.originatedUrl,
    });
  } else {
    return response;
  }
};

export const timeout = ms => new Promise(resolve => { setTimeout(resolve, ms); });

// Consider using the ajaxFetch that lives in ~/dashboard/helpers.jsx
// It stringifies the body, applies the json header,
// converts the request body from camelCase to snake_case
// and converts the response body from snake_case to camelCase.
export const MAX_FETCH_RETRIES = 1;

export const ajaxFetch = async (
  url,
  options = {},
  callback = false,
  retries = MAX_FETCH_RETRIES,
) => {
  let urlPath = url;
  if (!url.startsWith(window.location.origin)) urlPath = `${window.location.origin}${url}`;
  const originatedUrlPath = window.location.pathname;

  try {
    const response = await fetch(urlPath, getDaffyFetchOptions(options));
    if (callback && typeof callback === 'function') {
      return callback(response);
    }
    return handleDaffyFetchResponse(response, {
      ...options, url, originatedUrl: originatedUrlPath,
    });
  } catch (error) {
    if (retries) {
      // Small back-off of few hundred ms that increases for subsequent retries
      await timeout(((MAX_FETCH_RETRIES - retries) * 100) + 100);
      return ajaxFetch(url, options, callback, retries - 1);
    }

    captureExceptionsToSentry(error, {
      method: options.method || 'GET',
      url,
      originatedUrl: originatedUrlPath,
      retriesLeft: retries,
    });
    return Promise.reject(error);
  }
};


const getTyphonFetchOptions = options => ({
  method: 'GET',
  ...options,
  credentials: 'include', // need to send cookies to typhon backend for permissions
  headers: {
    'X-Requested-With': 'XMLHttpRequest',
    'Content-Type': 'application/json',
    ...(options?.headers || {}),
  },
});

export const EMPTY_204_RESPONSE = '';

const handleTyphonFetchResponse = (response, options) => {
  // return the response object for specific handling
  if (options?.returnResponse) {
    return response;
  }
  if (response.status === 200
    || response.status === 201
    || response.status === 202
  ) {
    if (Object.keys(options).includes('json') && !options.json) {
      // If options: { json: false }, just return the response as is without JSON parsing
      return response.text();
    }

    // By default, handle all responses as JSON
    return response.json();
  }
  if (response.status === 204) {
    return EMPTY_204_RESPONSE;
  }
  if (!options.silent) {
    if (response.status === 403) {
      flashMessages(['Data request forbidden. You don\'t have appropriate permissions.']);
    } else {
      if (response.status && response.status >= 400 && response.status < 500
        && options.redirectOnError) {
        window.location.replace(`/v3/dashboard/error/${response.status}?linkUrl=${window.location.pathname}`);
      }
      genericErrorMessage();
    }
  }
  return response.json().then(err => {
    throw new ResponseException(response, { url: options.url, method: options.method }, err);
  });
};

export const typhonAjaxFetch = async (
  url,
  options = {},
  callback = false,
  retries = MAX_FETCH_RETRIES,
) => {
  const originatedUrlPath = window.location.pathname;
  try {
    const test = getTyphonFetchOptions(options);
    const response = await fetch(url, test);
    if (callback && typeof callback === 'function') {
      return callback(response);
    }
    return handleTyphonFetchResponse(response, {
      ...options, url, originatedUrl: originatedUrlPath,
    });
  } catch (error) {
    if (retries) {
      // Small back-off of few hundred ms that increases for subsequent retries
      await timeout(((MAX_FETCH_RETRIES - retries) * 100) + 100);
      return typhonAjaxFetch(url, options, callback, retries - 1);
    }

    captureExceptionsToSentry(error, {
      method: options.method || 'GET',
      url,
      originatedUrl: originatedUrlPath,
      retriesLeft: retries,
    });
    return Promise.reject(error);
  }
};

export const typhonMultipartFormDataRequest = async (url, options) => {
  const originatedUrlPath = window.location.pathname;
  try {
    const response = await fetch(url, {
      method: 'PUT',
      ...options,
      credentials: 'include', // need to send cookies to typhon backend for permissions
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
    });
    return handleTyphonFetchResponse(response, {
      ...options, url, originatedUrl: originatedUrlPath,
    });
  } catch (error) {
    captureExceptionsToSentry(error, {
      method: options.method || 'PUT',
      url,
      originatedUrl: originatedUrlPath,
    });
    return Promise.reject(error);
  }
};


// Convert an object of params to a GET URL query string
export function buildUrlParamsString(params, jsonify = false) {
  const searchParams = new URLSearchParams();
  if (params) {
    Object.keys(params)
      .forEach(key => {
        const value = jsonify ? JSON.stringify(params[key]) : params[key];
        searchParams.append(key, value);
      });
  }

  return `?${searchParams.toString()}`;
}

// Convert an object of params to a GET URL query string and append LZW-compressed q param
export function buildUrlParamsStringWithCompressedQuery(params, compressedQuery, jsonify = false) {
  const searchParams = new URLSearchParams();
  if (params) {
    Object.keys(params)
      .forEach(key => {
        const value = jsonify ? JSON.stringify(params[key]) : params[key];
        searchParams.append(key, value);
      });
  }
  return `?${searchParams.toString()}&q=${compressedQuery}&qIsCompressed=true`;
}

// Get any parameters from a passed URL or the URL of the current page
export function getUrlParams(url = '') {
  const params = {};
  const path = url || window.location.href;
  path.replace(location.hash, '')
    .replace(
      /[?&]+([^=&]+)=?([^&]*)?/gi,
      (m, key, value) => {
        params[key] = value !== undefined ? value : '';
      },
    );
  return params;
}

export const getURLParams = getUrlParams;
export const buildURLParamsString = buildUrlParamsString;

export function buildUrl(relativePath, params = { json: true }) {
  const baseUrl = window.location.origin;
  const urlParams = buildURLParamsString(params, true);
  if (relativePath[relativePath.length - 1] === '/') {
    return `${baseUrl}${relativePath}${urlParams}`;
  }
  return `${baseUrl}${relativePath}/${urlParams}`;
}

export const fetchDaffy = (
  url,
  {
    body,
    hasSilentAlertMessage,
    method,
    returnResponse = false,
  } = {},
) => ajaxFetch(url, {
  body: JSON.stringify(body),
  headers: { 'Content-Type': 'application/json' },
  method: method || 'GET',
  silent: hasSilentAlertMessage,
  returnResponse,
});

export const fetchTyphon = (
  url,
  { body, hasSilentAlertMessage, method } = {},
) => typhonAjaxFetch(url, {
  body: JSON.stringify(body),
  method: method || 'GET',
  silent: hasSilentAlertMessage,
});
